import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuthStore } from '../stores/auth';

import { ChatHeader, HeaderTab } from './chat/ChatHeader';
import { ChatInput } from './chat/ChatInput';
import { MessageContainer } from './chat/MessageContainer';
import { ProfileMenu } from './shared/ProfileMenu';
import { useChatMessages } from '../hooks/useChatMessages';
import { useVoiceRecording } from '../hooks/useVoiceRecording';
import { useFileUpload } from '../hooks/useFileUpload';
import ActionButtons from './ActionButtons';
import { ProjectView } from './projects/ProjectView';
import { ProjectsHub, PROJECTS_HUB_ID } from './projects/ProjectsHub';
import { HomeworkPortal } from './homework/HomeworkPortal';
import { EnglishSkillsPortal } from './english/EnglishSkillsPortal';
import { RevisionPortal } from './revision/RevisionPortal';
import { StudentCalendar } from './calendar/StudentCalendar';
import { StudentProgressDashboard } from './progress/StudentProgressDashboard';
import { FeedbackModal } from './shared/FeedbackModal';
import { ProductTour } from './ProductTour';
import { toast } from 'react-hot-toast';

import { englishService } from '../services/englishService';
import { User } from '../types/auth';
import { EnglishState } from '../types/english';
import { UserRole } from '../types/roles';

export interface ChatLayoutProps {
  userRole: UserRole;
  selectedSubject: string | null;
  onSubjectSelect: (subjectId: string | null) => void;
  selectedThreadId: string | null;
  onThreadSelect: (threadId: string) => void;
  selectedProjectId: string | null;
  onProjectSelect: (projectId: string | null) => void;
}

const ChatLayout: React.FC<ChatLayoutProps> = ({
  userRole,
  selectedSubject,
  onSubjectSelect,
  selectedThreadId,
  onThreadSelect,
  selectedProjectId,
  onProjectSelect
}) => {
  const [inputText, setInputText] = useState('');
  const [composerMode, setComposerMode] = useState<'default' | 'projects' | 'homework' | 'revision'>('default');
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [subscribeSubject, setSubscribeSubject] = useState('');
  const [unsubscribeSubject, setUnsubscribeSubject] = useState('');
  const [activeTab, setActiveTab] = useState<HeaderTab>('chat');
  const [activePanel, setActivePanel] = useState<'none' | 'homework' | 'revision' | 'english-skills'>('none');
  const [isClarificationOtherEnabled, setIsClarificationOtherEnabled] = useState(false);
  const [isOnboardingSubscribeBusy, setIsOnboardingSubscribeBusy] = useState<string | null>(null);
  const [isUpsellDismissBusy, setIsUpsellDismissBusy] = useState(false);
  const [isUpsellHiddenLocally, setIsUpsellHiddenLocally] = useState(false);
  const [englishState, setEnglishState] = useState<EnglishState | null>(null);
  const [isEnglishStateLoading, setIsEnglishStateLoading] = useState(false);
  const [isEnglishNudgeBusy, setIsEnglishNudgeBusy] = useState(false);
  const [prefilledCompositionPrompt, setPrefilledCompositionPrompt] = useState<string | null>(null);

  const { user, logout } = useAuthStore();

  const chatMessages = useChatMessages({ selectedSubject, selectedThreadId });
  
  const voiceRecording = useVoiceRecording({
    selectedSubject,
    selectedThreadId,
    onMessageSent: () => {
      chatMessages.fetchUserSubjects();
    },
    onPendingMessage: chatMessages.addPendingMessage
  });
  
  const fileUpload = useFileUpload({
    selectedSubject,
    selectedThreadId,
    onMessageSent: () => {
      chatMessages.fetchUserSubjects();
    },
    onPendingMessage: chatMessages.addPendingMessage
  });

  const getInitials = (user?: User): string => {
    if (!user?.firstName || !user?.lastName) return '...';
    return `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`.toUpperCase();
  };

  const getFirstName = (user?: User): string => {
    if (!user?.firstName) return 'there';
    return user.firstName;
  };

  const userInitials = user ? getInitials(user) : '...';
  const userFirstName = user ? getFirstName(user) : 'there';

  const selectedSubjectDetails = useMemo(() => {
    if (!selectedSubject || !chatMessages.userSubjects) return null;
    return chatMessages.userSubjects.find((subject) => subject.subject_id === selectedSubject) || null;
  }, [selectedSubject, chatMessages.userSubjects]);

  const selectedSubjectName = selectedSubjectDetails?.name || null;
  const selectedSubjectCapabilities = useMemo(
    () =>
      selectedSubjectDetails?.capabilities || {
        english_skills: false,
        english_exam_reading: false,
        english_oral_reading: false,
        english_speaking: false,
      },
    [selectedSubjectDetails]
  );
  const isSelectedSubjectEnglishSkills = Boolean(selectedSubject && selectedSubjectCapabilities.english_skills);

  const realSubjects = useMemo(
    () => chatMessages.userSubjects.filter((subject) => subject.subject_id !== 'general'),
    [chatMessages.userSubjects]
  );
  const hasRealSubjects = realSubjects.length > 0;
  const shouldShowUpsellModal =
    hasRealSubjects &&
    !chatMessages.hasSeenSubjectUpsell &&
    !isUpsellHiddenLocally &&
    chatMessages.subscribableSubjects.length > 0;

  const activeClarificationMessageId = useMemo(() => {
    for (let i = chatMessages.allMessages.length - 1; i >= 0; i -= 1) {
      const message = chatMessages.allMessages[i];
      if (message.role !== 'assistant' && message.role !== 'user') {
        continue;
      }
      if (message.role === 'user') {
        return null;
      }
      const options = message.clarification?.options;
      if (Array.isArray(options) && options.length > 0) {
        return message.id;
      }
      return null;
    }
    return null;
  }, [chatMessages.allMessages]);
  const activeClarificationPayload = useMemo(() => {
    if (!activeClarificationMessageId) {
      return null;
    }
    const message = chatMessages.allMessages.find((item) => item.id === activeClarificationMessageId);
    return message?.clarification || null;
  }, [activeClarificationMessageId, chatMessages.allMessages]);
  const clarificationOtherOptionLabel =
    String(activeClarificationPayload?.other_option_label || '').trim() || 'Other (type my own answer)';

  useEffect(() => {
    setIsClarificationOtherEnabled(false);
  }, [activeClarificationMessageId]);

  const isClarificationComposerLocked = Boolean(activeClarificationMessageId && !isClarificationOtherEnabled);

  useEffect(() => {
    if (hasRealSubjects) {
      const hasSelectedRealSubject = !!selectedSubject && realSubjects.some((subject) => subject.subject_id === selectedSubject);
      if (!hasSelectedRealSubject) {
        onSubjectSelect(realSubjects[0].subject_id);
      }
      return;
    }

    if (selectedSubject) {
      onSubjectSelect(null);
    }
  }, [hasRealSubjects, onSubjectSelect, realSubjects, selectedSubject]);

  useEffect(() => {
    if (chatMessages.hasSeenSubjectUpsell) {
      setIsUpsellHiddenLocally(true);
    }
  }, [chatMessages.hasSeenSubjectUpsell]);

  const refreshEnglishState = useCallback(async () => {
    if (!selectedSubject || !selectedSubjectCapabilities.english_skills) {
      setEnglishState(null);
      return;
    }
    setIsEnglishStateLoading(true);
    try {
      const nextState = await englishService.getState(selectedSubject);
      setEnglishState(nextState);
    } catch (error) {
      console.error('Failed to load English state:', error);
      setEnglishState(null);
    } finally {
      setIsEnglishStateLoading(false);
    }
  }, [selectedSubject, selectedSubjectCapabilities.english_skills]);

  useEffect(() => {
    void refreshEnglishState();
  }, [refreshEnglishState]);

  useEffect(() => {
    setPrefilledCompositionPrompt(null);
  }, [selectedSubject]);

  const handleLogout = async () => {
    setIsProfileOpen(false);
    await logout();
  };

  const handleSubjectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newSubject = e.target.value;
    onSubjectSelect(newSubject || null);
    onThreadSelect('');
  };

  const handleSendMessage = async (overrideText?: unknown) => {
    const candidateText = typeof overrideText === 'string' ? overrideText : inputText;
    const effectiveText = String(candidateText ?? '').trim();
    if (!effectiveText && !fileUpload.fileToUpload) return;
    if (!selectedSubject) {
      toast.error('Subscribe to a subject to start chatting.');
      return;
    }

    const currentThreadId = selectedThreadId || `temp-${Date.now()}`;
    const messageContent = effectiveText;
    const contextType = composerMode !== 'default' 
      ? (composerMode === 'projects' ? 'project' : composerMode) 
      : undefined;
    
    setInputText('');
    
    const tempId = `temp-text-${Date.now()}`;
    const pendingMessage = {
      id: tempId,
      content: messageContent,
      thread_id: currentThreadId,
      message_type: 'TEXT' as const,
      role: 'user' as const,
      sequence_number: 0,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      status: 'sending' as const,
      tempMediaUrl: '',
      fileName: '',
      fileSize: 0,
      fileType: ''
    };

    try {
      if (fileUpload.fileToUpload) {
        const selectedFile = fileUpload.fileToUpload;
        const lowerName = selectedFile.name.toLowerCase();
        const showImageProcessingHint = selectedFile.type.startsWith('image/');
        const showAttachmentProcessingHint =
          !showImageProcessingHint &&
          (
            selectedFile.type.includes('pdf') ||
            selectedFile.type.includes('msword') ||
            selectedFile.type.includes('officedocument.wordprocessingml.document') ||
            lowerName.endsWith('.pdf') ||
            lowerName.endsWith('.doc') ||
            lowerName.endsWith('.docx')
          );
        const processingHint = showImageProcessingHint
          ? 'image_processing'
          : showAttachmentProcessingHint
            ? 'attachment_processing'
            : null;
        const processingMessageId = processingHint ? `temp-upload-processing-${Date.now()}` : null;

        // Start the upload first so the pending learner media bubble is created before this assistant hint.
        const uploadPromise = fileUpload.uploadFile(selectedFile, messageContent, contextType);

        if (processingMessageId && processingHint) {
          const indicatorTimestamp = new Date(Date.now() + 1500).toISOString();
          chatMessages.addPendingMessage({
            id: processingMessageId,
            content: '',
            thread_id: currentThreadId,
            message_type: 'TEXT',
            role: 'assistant',
            sequence_number: 0,
            created_at: indicatorTimestamp,
            updated_at: indicatorTimestamp,
            status: 'sending',
            pendingHint: processingHint,
          });
        }

        try {
          const uploaded = await uploadPromise;

          if (
            uploaded?.ai_message_id &&
            uploaded?.clarification &&
            Array.isArray(uploaded.clarification.options) &&
            uploaded.clarification.options.length > 0
          ) {
            chatMessages.registerClarification(uploaded.ai_message_id, uploaded.clarification);
          }

          await chatMessages.fetchUserSubjects();

          if (uploaded?.subject_id) {
            if (!selectedSubject || selectedSubject !== uploaded.subject_id) {
              onSubjectSelect(uploaded.subject_id);
            }
          }
          if (uploaded?.thread_id) {
            onThreadSelect(uploaded.thread_id);
          }
        } finally {
          if (processingMessageId) {
            chatMessages.removePendingMessage(processingMessageId);
          }
        }
      } else {
        chatMessages.addPendingMessage(pendingMessage);
        chatMessages.updatePendingMessageStatus(tempId, 'sent');
        
        const response = await chatMessages.sendTextMessageStream(messageContent, undefined, contextType);
        
        console.log('ChatLayout: sendTextMessageStream returned:', response);
        
        if (response?.subject_id) {
          if (!selectedSubject || selectedSubject !== response.subject_id) {
            console.log('ChatLayout: Updating subject to:', response.subject_id);
            onSubjectSelect(response.subject_id);
          }
        }
        if (response?.thread_id) {
          console.log('ChatLayout: Updating thread to real ID:', response.thread_id);
          onThreadSelect(response.thread_id);
        }
      }
    } catch (error) {
      console.error('Error sending message:', error);
      if (!fileUpload.fileToUpload) {
        chatMessages.updatePendingMessageStatus(tempId, 'failed');
      }
    }
  };

  const handleClarificationOptionSelect = async (optionId: string) => {
    setIsClarificationOtherEnabled(false);
    await handleSendMessage(optionId);
  };

  const handleEnableClarificationOther = () => {
    setIsClarificationOtherEnabled(true);
  };

  const handleSubscribeSubject = async () => {
    if (!subscribeSubject) return;
    await chatMessages.fetchUserSubjects();
    onSubjectSelect(subscribeSubject);
    onThreadSelect('');
    setSubscribeSubject('');
  };

  const handleUnsubscribeSubject = async (subjectId?: string) => {
    const targetSubjectId = String(subjectId || unsubscribeSubject).trim();
    if (!targetSubjectId) return;
    await chatMessages.unsubscribeFromSubject(targetSubjectId);
    if (targetSubjectId === unsubscribeSubject) {
      setUnsubscribeSubject('');
    }
  };

  const handleOnboardingSubscribe = async (subjectId: string) => {
    setIsOnboardingSubscribeBusy(subjectId);
    try {
      const ok = await chatMessages.subscribeToSubject(subjectId);
      if (ok) {
        onSubjectSelect(subjectId);
      }
    } finally {
      setIsOnboardingSubscribeBusy(null);
    }
  };

  const dismissUpsell = async () => {
    if (isUpsellDismissBusy) return;
    setIsUpsellDismissBusy(true);
    try {
      await chatMessages.markSubjectUpsellSeen();
      setIsUpsellHiddenLocally(true);
    } finally {
      setIsUpsellDismissBusy(false);
    }
  };

  const handleTabChange = (tab: HeaderTab) => {
    setActiveTab(tab);
    if (tab !== 'chat') {
      setActivePanel('none');
    }
  };

  const handleActionClick = (action: string) => {
    switch (action) {
      case 'projects': {
        setComposerMode('projects');
        setInputText('');
        setActivePanel('none');
        onProjectSelect(PROJECTS_HUB_ID);
        break;
      }
      case 'homework': {
        setComposerMode('homework');
        setInputText('');
        setActivePanel('homework');
        onProjectSelect(null);
        break;
      }
      case 'revision': {
        setComposerMode('revision');
        setInputText('');
        setActivePanel('revision');
        onProjectSelect(null);
        break;
      }
      case 'english-skills': {
        setComposerMode('default');
        setInputText('');
        setActivePanel('english-skills');
        onProjectSelect(null);
        break;
      }
      case 'feedback': {
        setShowFeedbackModal(true);
        break;
      }
      default:
        break;
    }
  };

  const handleHomeworkSubmit = (message: string) => {
    setComposerMode('homework');
    setActivePanel('none');
    handleSendMessage(message);
  };

  const handleRevisionSubmit = (message: string) => {
    setComposerMode('revision');
    setActivePanel('none');
    handleSendMessage(message);
  };

  const dismissEnglishNudge = useCallback(async () => {
    if (!selectedSubject || isEnglishNudgeBusy) return;
    setIsEnglishNudgeBusy(true);
    try {
      await englishService.dismissNudge(selectedSubject);
      await refreshEnglishState();
    } catch (error) {
      console.error('Failed to dismiss English nudge:', error);
    } finally {
      setIsEnglishNudgeBusy(false);
    }
  }, [isEnglishNudgeBusy, refreshEnglishState, selectedSubject]);

  const handleEnglishCompositionPrompt = async (prompt: string) => {
    setComposerMode('default');
    setPrefilledCompositionPrompt(prompt);
    setActivePanel('english-skills');
    onProjectSelect(null);
  };

  const handleExitProjectsToChat = () => {
    onProjectSelect(null);
    setComposerMode('default');
    setActivePanel('none');
  };

  const modeLabel =
    composerMode === 'homework'
      ? 'Homework Mode'
      : composerMode === 'revision'
        ? 'Revision / Exam Coaching'
        : composerMode === 'projects'
          ? 'Projects Mode'
          : null;

  const modePlaceholder =
    composerMode === 'homework'
      ? "Paste your homework question and what you've tried…"
      : composerMode === 'revision'
        ? 'Tell me the subject + topic you are revising (and exam date if you know it)…'
        : composerMode === 'projects'
          ? 'Describe your project and what you need help with…'
          : 'Type your message...';
  const clarificationPlaceholder = isClarificationComposerLocked
    ? 'Choose one option above to continue.'
    : activeClarificationMessageId && isClarificationOtherEnabled
      ? 'Type your own answer (or click an option above)...'
      : null;

  const profileMenuProps = {
    user,
    isOpen: isProfileOpen,
    onClose: () => setIsProfileOpen(false),
    onLogout: handleLogout,
    showSubscriptionManagement: true,
    subscribeSubject,
    setSubscribeSubject,
    handleSubscribeSubject,
    unsubscribeSubject,
    setUnsubscribeSubject,
    handleUnsubscribeSubject,
    subscribableSubjects: chatMessages.subscribableSubjects,
    subscribedSubjects: chatMessages.subscribedSubjects
  };

  if (!user?.isAuthenticated) {
    return null;
  }

  const renderEnglishNudgeCard = () => {
    if (!isSelectedSubjectEnglishSkills || isEnglishStateLoading || !englishState?.nudge_due || activePanel !== 'none') {
      return null;
    }
    return (
      <div className="mb-4 rounded-2xl border border-amber-500/25 bg-amber-500/10 p-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-sm font-semibold text-amber-100">Weekly composition practice</p>
            <p className="mt-1 text-xs text-amber-50/80">
              Choose a prompt, then take clear photos of your written composition and upload them for marking.
            </p>
          </div>
          <button
            type="button"
            onClick={() => void dismissEnglishNudge()}
            disabled={isEnglishNudgeBusy}
            className="px-2.5 py-1 rounded-md border border-amber-400/30 text-[11px] text-amber-100 hover:bg-amber-500/15 disabled:opacity-60"
          >
            {isEnglishNudgeBusy ? 'Saving...' : 'Maybe later'}
          </button>
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          {(englishState.suggested_composition_prompts || []).slice(0, 3).map((prompt) => (
            <button
              key={prompt}
              type="button"
              onClick={() => void handleEnglishCompositionPrompt(prompt)}
              className="text-left rounded-full border border-amber-400/25 bg-[var(--surface-elevated)] px-3 py-2 text-xs text-[var(--text-primary)] hover:bg-[var(--hover-bg)] transition-colors"
            >
              {prompt}
            </button>
          ))}
        </div>
      </div>
    );
  };

  const renderEnglishSkillsShortcut = () => {
    if (!isSelectedSubjectEnglishSkills || activePanel !== 'none') {
      return null;
    }
    return (
      <div className="mb-3 flex items-center justify-between gap-3 rounded-xl border border-amber-500/20 bg-amber-500/5 px-3 py-2">
        <div>
          <p className="text-xs font-medium text-amber-100">Reading &amp; Composition</p>
          <p className="text-[11px] text-amber-50/75">
            Open reading practice, composition marking, and optional live coaching.
          </p>
        </div>
        <button
          type="button"
          onClick={() => handleActionClick('english-skills')}
          className="px-3 py-1.5 rounded-md bg-amber-500 text-white text-xs font-medium hover:bg-amber-600 transition-colors"
        >
          Open
        </button>
      </div>
    );
  };

  const renderChatView = () => {
    if (!hasRealSubjects) {
      return (
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-3xl mx-auto p-6 sm:p-8">
            <div className="rounded-2xl border border-[var(--border-color)] bg-[var(--surface-elevated)] p-6 space-y-4">
              <h2 className="text-lg font-semibold text-[var(--text-primary)]">Choose your first subject</h2>
              <p className="text-sm text-[var(--text-secondary)]">
                You need at least one active subject subscription before chat unlocks. Subscribe below, or open
                Manage Subjects from your profile to explore all options.
              </p>

              <div className="space-y-2">
                {chatMessages.subscribableSubjects.length === 0 ? (
                  <p className="text-sm text-[var(--text-secondary)]">
                    No available subjects found for your level right now.
                  </p>
                ) : (
                  chatMessages.subscribableSubjects.map((subject) => (
                    <div
                      key={subject.id}
                      className="flex items-center justify-between gap-3 rounded-xl border border-[var(--border-color)] px-3 py-2"
                    >
                      <div>
                        <p className="text-sm text-[var(--text-primary)]">{subject.name}</p>
                        {subject.monthly_price_label && (
                          <p className="text-xs text-[var(--text-secondary)]">{subject.monthly_price_label}</p>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => handleOnboardingSubscribe(subject.id)}
                        disabled={isOnboardingSubscribeBusy === subject.id}
                        className="px-3 py-1.5 rounded-md bg-[var(--accent-primary)] text-white text-xs font-medium disabled:opacity-60"
                      >
                        {isOnboardingSubscribeBusy === subject.id ? 'Subscribing...' : 'Subscribe'}
                      </button>
                    </div>
                  ))
                )}
              </div>

              <button
                type="button"
                onClick={() => setIsProfileOpen(true)}
                className="px-3 py-2 rounded-lg border border-[var(--border-color)] text-sm text-[var(--text-primary)] hover:bg-[var(--hover-bg)] transition-colors"
              >
                Open Manage Subjects
              </button>
            </div>
          </div>
        </div>
      );
    }

    if (selectedProjectId) {
      return (
        <div className="flex-1 flex flex-col min-h-0">
          {selectedProjectId === PROJECTS_HUB_ID ? (
            <div className="flex-1 overflow-y-auto">
              <ProjectsHub
                onSelectProject={(id) => onProjectSelect(id)}
                onExitToChat={handleExitProjectsToChat}
              />
            </div>
          ) : (
            <ProjectView
              key={selectedProjectId}
              projectId={selectedProjectId}
              onBack={() => onProjectSelect(PROJECTS_HUB_ID)}
              onExitToChat={handleExitProjectsToChat}
            />
          )}
        </div>
      );
    }

    if (activePanel === 'homework') {
      return (
        <div className="flex-1 overflow-y-auto">
          <HomeworkPortal
            subjectName={selectedSubjectName}
            onSubmit={handleHomeworkSubmit}
            onBack={() => { setActivePanel('none'); setComposerMode('default'); }}
          />
        </div>
      );
    }

    if (activePanel === 'revision') {
      return (
        <div className="flex-1 overflow-y-auto">
          <RevisionPortal
            subjectName={selectedSubjectName}
            subjects={realSubjects}
            onSubmit={handleRevisionSubmit}
            onBack={() => { setActivePanel('none'); setComposerMode('default'); }}
          />
        </div>
      );
    }

    if (activePanel === 'english-skills' && selectedSubject) {
      return (
        <div className="flex-1 overflow-y-auto">
          <EnglishSkillsPortal
            subjectId={selectedSubject}
            subjectName={selectedSubjectName}
            englishState={englishState}
            onBack={() => setActivePanel('none')}
            onStateRefresh={refreshEnglishState}
            initialCompositionPrompt={prefilledCompositionPrompt}
          />
        </div>
      );
    }

    if (chatMessages.allMessages.length > 0) {
      return (
        <>
          <div className="flex-1 overflow-y-auto">
            <MessageContainer
              messages={chatMessages.allMessages}
              selectedSubject={selectedSubject}
              selectedSubjectName={selectedSubjectName}
              userFirstName={userFirstName}
              isProfileOpen={isProfileOpen}
              messagesEndRef={chatMessages.messagesEndRef}
              onDeleteMessage={chatMessages.deleteMessage}
              onSelectClarificationOption={handleClarificationOptionSelect}
              onEnableClarificationOther={handleEnableClarificationOther}
            />
          </div>

          <div className="flex-shrink-0 border-t border-[var(--border-color)] bg-[var(--bg-primary)]">
            <div className="max-w-3xl mx-auto p-4">
              {renderEnglishNudgeCard()}
              {renderEnglishSkillsShortcut()}
              {isClarificationComposerLocked && (
                <div className="mb-3 rounded-xl border border-cyan-500/30 bg-cyan-500/10 px-3 py-2 flex items-center justify-between gap-3">
                  <p className="text-xs text-cyan-100">Choose one option above to continue.</p>
                  <button
                    type="button"
                    onClick={handleEnableClarificationOther}
                    className="px-2.5 py-1 rounded-md border border-cyan-400/40 text-[11px] text-cyan-200 hover:bg-cyan-500/20 transition-colors"
                  >
                    {clarificationOtherOptionLabel}
                  </button>
                </div>
              )}
              <ChatInput
                inputText={inputText}
                setInputText={setInputText}
                fileToUpload={fileUpload.fileToUpload}
                onFileClick={() => {}}
                onFileChange={fileUpload.handleFileSelect}
                onSendMessage={handleSendMessage}
                isRecording={voiceRecording.isRecording}
                onToggleRecording={voiceRecording.toggleRecording}
                acceptedFileTypes={fileUpload.acceptedFileTypes}
                disabled={fileUpload.isUploading || voiceRecording.isUploading || isClarificationComposerLocked}
                onClearFile={fileUpload.clearFile}
                modeLabel={modeLabel}
                placeholder={clarificationPlaceholder || modePlaceholder}
                onClearMode={() => setComposerMode('default')}
              />
            </div>
          </div>
        </>
      );
    }

    return (
      <div className="flex-1 flex flex-col items-center justify-center px-4 py-8">
        <MessageContainer
          messages={chatMessages.allMessages}
          selectedSubject={selectedSubject}
          selectedSubjectName={selectedSubjectName}
          userFirstName={userFirstName}
          isProfileOpen={isProfileOpen}
          messagesEndRef={chatMessages.messagesEndRef}
          onDeleteMessage={chatMessages.deleteMessage}
          onSelectClarificationOption={handleClarificationOptionSelect}
          onEnableClarificationOther={handleEnableClarificationOther}
        />
        
        <div className="w-full max-w-2xl mt-8 space-y-5">
          {renderEnglishNudgeCard()}
          {isClarificationComposerLocked && (
            <div className="rounded-xl border border-cyan-500/30 bg-cyan-500/10 px-3 py-2 flex items-center justify-between gap-3">
              <p className="text-xs text-cyan-100">Choose one option above to continue.</p>
              <button
                type="button"
                onClick={handleEnableClarificationOther}
                className="px-2.5 py-1 rounded-md border border-cyan-400/40 text-[11px] text-cyan-200 hover:bg-cyan-500/20 transition-colors"
              >
                {clarificationOtherOptionLabel}
              </button>
            </div>
          )}
          <ChatInput
            inputText={inputText}
            setInputText={setInputText}
            fileToUpload={fileUpload.fileToUpload}
            onFileClick={() => {}}
            onFileChange={fileUpload.handleFileSelect}
            onSendMessage={handleSendMessage}
            isRecording={voiceRecording.isRecording}
            onToggleRecording={voiceRecording.toggleRecording}
            acceptedFileTypes={fileUpload.acceptedFileTypes}
            disabled={fileUpload.isUploading || voiceRecording.isUploading || isClarificationComposerLocked}
            onClearFile={fileUpload.clearFile}
            modeLabel={modeLabel}
            placeholder={clarificationPlaceholder || modePlaceholder}
            onClearMode={() => setComposerMode('default')}
          />
          
          <div className="space-y-4">
            <ActionButtons 
              onAction={handleActionClick}
              subjectName={selectedSubjectName}
              showEnglishSkills={Boolean(selectedSubject && selectedSubjectCapabilities.english_skills)}
            />
            <p className="text-center text-xs text-[var(--text-secondary)]/70">
              AI responses may occasionally be inaccurate
            </p>
          </div>
        </div>
      </div>
    );
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'calendar':
        return (
          <div className="flex-1 overflow-y-auto">
            <StudentCalendar subjects={realSubjects} />
          </div>
        );
      case 'progress':
        return (
          <div className="flex-1 overflow-y-auto">
            <StudentProgressDashboard />
          </div>
        );
      case 'chat':
      default:
        return renderChatView();
    }
  };

  return (
    <div className="flex-1 flex flex-col h-full bg-[var(--bg-primary)]">
      <ProductTour />
      <ChatHeader
        selectedSubject={selectedSubject}
        userSubjects={realSubjects}
        onSubjectChange={handleSubjectChange}
        userInitials={userInitials}
        isProfileOpen={isProfileOpen}
        onProfileToggle={() => setIsProfileOpen(!isProfileOpen)}
        activeTab={activeTab}
        onTabChange={handleTabChange}
      />

      <div className="relative">
        <ProfileMenu {...profileMenuProps} />
      </div>

      <div className={`flex-1 flex flex-col min-h-0 ${isProfileOpen ? 'blur-sm pointer-events-none' : ''}`}>
        {renderContent()}
      </div>

      <FeedbackModal
        isOpen={showFeedbackModal}
        onClose={() => setShowFeedbackModal(false)}
      />

      {shouldShowUpsellModal && (
        <div className="fixed inset-0 z-[80] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="w-full max-w-lg rounded-2xl border border-[var(--border-color)] bg-[var(--surface-elevated)] p-6">
            <h3 className="text-lg font-semibold text-[var(--text-primary)]">Add more subjects</h3>
            <p className="mt-2 text-sm text-[var(--text-secondary)]">
              You can subscribe to more subjects anytime to switch support areas from the subject picker.
            </p>

            <div className="mt-4 space-y-2 max-h-56 overflow-y-auto">
              {chatMessages.subscribableSubjects.slice(0, 6).map((subject) => (
                <div
                  key={subject.id}
                  className="flex items-center justify-between rounded-lg border border-[var(--border-color)] px-3 py-2"
                >
                  <div>
                    <p className="text-sm text-[var(--text-primary)]">{subject.name}</p>
                    {subject.monthly_price_label && (
                      <p className="text-xs text-[var(--text-secondary)]">{subject.monthly_price_label}</p>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => handleOnboardingSubscribe(subject.id)}
                    disabled={isOnboardingSubscribeBusy === subject.id}
                    className="px-2.5 py-1 rounded-md text-xs bg-[var(--accent-primary)] text-white disabled:opacity-60"
                  >
                    {isOnboardingSubscribeBusy === subject.id ? 'Subscribing...' : 'Subscribe'}
                  </button>
                </div>
              ))}
            </div>

            <div className="mt-5 flex justify-end">
              <button
                type="button"
                onClick={dismissUpsell}
                disabled={isUpsellDismissBusy}
                className="px-3 py-2 rounded-lg border border-[var(--border-color)] text-sm text-[var(--text-primary)] hover:bg-[var(--hover-bg)] disabled:opacity-60"
              >
                {isUpsellDismissBusy ? 'Saving...' : 'Close'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatLayout;
